// eslint-disable-next-line eslint-comments/disable-enable-pair
/* eslint-disable formatjs/no-literal-string-in-jsx */
import { useIntlSort } from "#/utils/hooks/useIntlSort"
import { UsaStates } from "#/utils/UsaStateOptions"
import {
  AvailableDate,
  AvailableTime,
  mockAppointmentTypeOptions,
  paymentMethodOptions
} from "#/features/telemedicine/booking/mockBookingData"
import { Divider, Progress, Typography } from "@carrotfertility/carotene-core"
import { FormAutocomplete, FormButton, FormRadioGroup, FormSelect, useWatch } from "@carrotfertility/carotene-core-x"
import React from "react"
import { useIntl } from "react-intl"

// Mirrors bookingMachine.ts's choosingDateTime compound state — passed in as an
// explicit prop instead of re-derived from watched form values.
type BookingStep = "enteringDetails" | "loadingAvailability" | "choosingDate" | "loadingTimes" | "choosingTime"

type BookingDetailsStepProps = {
  availableDates: AvailableDate[]
  availabilityError: boolean
  availableTimes: AvailableTime[]
  timesError: boolean
  step: BookingStep
}

// No <Form> here — this and every other step share the one FormProvider/useForm()
// instance created in BookingForm.tsx, so the submit button below is picked up by
// that shared <form>'s onSubmit rather than owning its own submission.
export function BookingDetailsStep({
  availableDates,
  availabilityError,
  availableTimes,
  timesError,
  step
}: BookingDetailsStepProps) {
  const intl = useIntl()
  const { localeComparator } = useIntlSort()

  const stateOptions: { label: string; value: string }[] = React.useMemo(
    () =>
      UsaStates.map((state) => ({
        label: intl.formatMessage(state.label),
        value: state.id
      })).sort((a, b) => localeComparator(a.label, b.label)),
    [intl, localeComparator]
  )

  const [appointmentType, locationOfCare, paymentMethod, selectedDate, selectedTime] = useWatch({
    name: ["appointment.type", "appointment.locationOfCare", "payment.method", "appointment.date", "appointment.time"]
  })

  const pastDetails = step !== "enteringDetails" && step !== "loadingAvailability"
  const detailsValid = Boolean(appointmentType && locationOfCare && paymentMethod)
  const ctaLabel =
    step === "enteringDetails"
      ? "See availability"
      : step === "loadingAvailability" || step === "loadingTimes"
      ? "Loading…"
      : "Continue"
  const ctaDisabled =
    step === "enteringDetails"
      ? !detailsValid
      : step === "loadingAvailability" || step === "loadingTimes"
      ? true
      : step === "choosingDate"
      ? !selectedDate
      : !selectedTime

  return (
    <>
      <Typography variant="h2">Initial consult</Typography>
      <Typography>
        Video visit · 30 minutes · with a Carrot Clinic clinician. Select a date and time that works for you.
      </Typography>

      <div>
        <FormSelect
          name="appointment.type"
          label="Appointment type"
          fullWidth
          options={mockAppointmentTypeOptions}
          controllerProps={{ rules: { required: true } }}
        />
        <Typography>You can schedule follow-up visits with the same provider after an initial consult.</Typography>
        <FormAutocomplete
          name="appointment.locationOfCare"
          fullWidth
          label="Location of care"
          options={stateOptions}
          controllerProps={{ rules: { required: true } }}
        />
        <FormSelect
          name="payment.method"
          label="Payment method"
          fullWidth
          options={paymentMethodOptions}
          controllerProps={{ rules: { required: true } }}
        />
      </div>

      {availabilityError && (
        <Typography sx={(theme) => ({ color: theme.palette.error.main })}>
          Couldn&apos;t load availability — try again.
        </Typography>
      )}

      {pastDetails && (
        <div>
          <Typography variant="h3">Select a date</Typography>
          <FormRadioGroup
            name="appointment.date"
            controllerProps={{ rules: { required: true } }}
            options={availableDates.map((date) => ({ value: date.value, label: date.label }))}
          />
        </div>
      )}

      {timesError && (
        <Typography sx={(theme) => ({ color: theme.palette.error.main })}>
          Couldn&apos;t load times for that date — try again.
        </Typography>
      )}

      {(step === "loadingTimes" || step === "choosingTime") && (
        <div>
          <Typography variant="h3">Select a time</Typography>
          {step === "loadingTimes" ? (
            <Progress size="1.25rem" />
          ) : (
            <FormRadioGroup
              name="appointment.time"
              controllerProps={{ rules: { required: true } }}
              options={availableTimes.map((time) => ({ value: time.value, label: time.label }))}
            />
          )}
        </div>
      )}

      <Divider />
      <FormButton type="submit" color="secondary" disabled={ctaDisabled}>
        {ctaLabel}
      </FormButton>
    </>
  )
}
